// @flow
/* eslint-disable camelcase */
import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Button from '@localytics/loc-components/lib/Button';
import Editor from '@localytics/push-message-builder/src/components/Editor';
import Preview from '@localytics/push-message-builder/src/components/Preview';
import DeleteNodeModal from './DeleteNodeModal';
import { StandardNode } from './StandardNode';
import { startDeletingNode, stopDeletingNode } from '../actions/tree';
import { MESSAGE_TYPE, ADD_NODE_TYPE } from '../constants/ApiTypes';
import { MESSAGE_ICON, MESSAGE_FILL_COLOR } from '../constants/Style';
import { DEFAULT_MESSAGE_DISPLAY_NAME } from './PushNode';
import './stylesheets/PushNodeModal.scss';
import ConfirmationDialog from './ConfirmationDialog';

type PushNodeModalProps = {
  creatives: Array<{
    id: number,
    creativeType: string,
    app_uuid: string,
    data: {
      title: string,
      body: string,
      attachment_url: string,
      attachment_type: string,
      deeplink: string,
      key_values: Object
    }
  }>,
  apps: Array<{
    appId: string,
    name: string,
    iconURL: string,
    platform: string
  }>,
  height: number,
  rx: number,
  ry: number,
  submitFormCallback: Function,
  cancelCallback?: Function,
  closeCallback: Function,
  nodeExistsInFlow: boolean,
  editable: boolean,
  displayName: ?string,
  deletingNode: boolean,
  dispatch: Function
};

type State = {
  creatives: {
    [number]: {
      title: string,
      body: string,
      attachment_url: string,
      attachment_type: string,
      deeplink: string,
      key_values: Object
    }
  },
  currentCreativeIndex: number,
  displayName: string,
  showConfirmationDialog: boolean
};

class PushNodeModal extends React.Component<PushNodeModalProps, State> {
  constructor(props: PushNodeModalProps) {
    super(props);
    const { creatives = [], displayName } = props;
    const creativeData = creatives.reduce(
      (creativesMap, c, index) => ({
        ...creativesMap,
        [index]: {
          ...c.data,
          deeplink: c.data.deeplink ? decodeURI(c.data.deeplink) : '',
          attachment_url: c.data.attachment_url || '',
          key_values: Object.keys(c.data.key_values).reduce(
            (kvMap, key) => ({
              keys: kvMap.keys.concat(key),
              values: kvMap.values.concat(c.data.key_values[key])
            }),
            { keys: [], values: [] }
          )
        }
      }),
      {}
    );
    this.isDirty = false;
    this.state = {
      creatives: creativeData,
      currentCreativeIndex: 0,
      displayName: displayName || DEFAULT_MESSAGE_DISPLAY_NAME,
      showConfirmationDialog: false
    };
  }

  componentWillUpdate() {
    this.isDirty = true;
  }

  setKeyValues: Function = newKeyValues => {
    const { creatives, currentCreativeIndex } = this.state;
    const creative = creatives[currentCreativeIndex];
    this.setState({
      creatives: {
        ...creatives,
        [currentCreativeIndex]: {
          ...creative,
          key_values: newKeyValues
        }
      }
    });
  };

  isDirty: boolean;

  submitForm: Function = () => {
    const { creatives: creativeData, displayName } = this.state;
    const reconstitutedCreatives = this.props.creatives.map(
      (creative, index) => {
        const creativeFromState = creativeData[index];
        const { deeplink, attachment_url, key_values } = creativeFromState;
        return {
          ...creative,
          data: {
            ...creativeFromState,
            deeplink: deeplink ? encodeURI(deeplink) : '',
            attachment_url: attachment_url || null,
            key_values: key_values.keys.reduce(
              (kvs, key, idx) => ({
                ...kvs,
                [key]: key_values.values[idx]
              }),
              {}
            )
          }
        };
      }
    );
    this.props.submitFormCallback({
      creatives: reconstitutedCreatives,
      node_type: MESSAGE_TYPE,
      display_name: displayName
    });
  };

  close: Function = () => {
    if (this.props.editable && this.isDirty) {
      this.setState({
        showConfirmationDialog: true
      });
    } else {
      this.closeForReal();
    }
  };

  closeForReal: Function = () => {
    this.props.closeCallback();
  };

  cancel: Function = () => {
    (this.props.cancelCallback
      ? this.props.cancelCallback
      : this.props.closeCallback)();
  };

  switchCreatives: Function = index => {
    this.setState({
      currentCreativeIndex: index
    });
  };

  submitFormField: Function = (key, value) => {
    const { creatives, currentCreativeIndex } = this.state;
    const creative = creatives[currentCreativeIndex];
    const newData = { [key]: value };
    let newCreatives = creatives;

    switch (key) {
      // Derive attachment_type from the ending of attachment_url.
      // May want to add selector in the future.
      case 'attachment_url': {
        let attachment_type = null;
        const fileTypes = ['png', 'gif', 'jpg', 'jpeg'];
        try {
          const { pathname } = new URL(value);
          fileTypes.forEach(fileType => {
            if (pathname.endsWith(`.${fileType}`)) {
              attachment_type = fileType;
            }
          });
        } catch (e) {
          console.log(e);
        }

        newCreatives = {
          ...creatives,
          [currentCreativeIndex]: {
            ...creative,
            ...newData,
            attachment_type
          }
        };
        break;
      }
      // Changes to title and body should apply across all creatives,
      // not just the one currently being edited
      case 'title':
      case 'body': {
        newCreatives = Object.keys(creatives)
          .map(creativeId => ({
            payload: {
              ...creatives[Number(creativeId)],
              ...newData
            },
            creativeId
          }))
          .reduce(
            (acc, { creativeId, payload }) => ({
              ...acc,
              [creativeId]: {
                ...payload
              }
            }),
            {}
          );
        break;
      }
      default: {
        newCreatives = {
          ...creatives,
          [currentCreativeIndex]: {
            ...creative,
            ...newData
          }
        };
        break;
      }
    }

    this.setState({
      creatives: {
        ...newCreatives
      }
    });
  };

  updateKV: Function = (kvLabel, kvInput, index) => {
    const { creatives, currentCreativeIndex } = this.state;
    const creative = creatives[currentCreativeIndex];
    const { key_values: { [kvLabel]: array } } = creative;
    const newArray = array.map((el, i) => (Number(index) === i ? kvInput : el));
    const newKeyValues = {
      ...creative.key_values,
      [kvLabel]: newArray
    };
    this.setState({
      creatives: {
        ...creatives,
        [currentCreativeIndex]: {
          ...creative,
          key_values: newKeyValues
        }
      }
    });
  };

  updateKVKeys: Function = (newKey, index) => {
    this.updateKV('keys', newKey, index);
  };

  updateKVValues: Function = (newValue, index) => {
    this.updateKV('values', newValue, index);
  };

  addEmptyKeyValue: Function = () => {
    const { creatives, currentCreativeIndex } = this.state;
    const creative = creatives[currentCreativeIndex];
    const { key_values: { keys, values } } = creative;
    const newKeyValues = {
      keys: keys.concat(''),
      values: values.concat('')
    };
    this.setKeyValues(newKeyValues);
  };

  removeKeyValue: Function = index => {
    const { creatives, currentCreativeIndex } = this.state;
    const creative = creatives[currentCreativeIndex];
    const { key_values: { keys, values } } = creative;
    const newKeyValues = {
      keys: keys.filter((el, i) => Number(index) !== i),
      values: values.filter((el, i) => Number(index) !== i)
    };
    this.setKeyValues(newKeyValues);
  };

  toggleShowDeleteModal: Function = () => {
    const { deletingNode, dispatch } = this.props;
    if (deletingNode) {
      dispatch(stopDeletingNode());
    } else {
      dispatch(startDeletingNode());
    }
  };

  hideConfirmationDialog: Function = () => {
    this.setState({ showConfirmationDialog: false });
  };

  updateDisplayName: Function = ({ target = {} }) => {
    this.setState({
      displayName: target.value
    });
  };

  checkForEmptyName: Function = ({ target = {} }) => {
    if (/^\s*$/.test(target.value) || target.value !== this.state.displayName) {
      this.setState({
        displayName: target.value || DEFAULT_MESSAGE_DISPLAY_NAME
      });
    }
  };

  focusMessageNameInput: Function = input => {
    if (input) {
      input.select();
    }
  };

  render() {
    const {
      height,
      rx,
      ry,
      apps = {},
      nodeExistsInFlow,
      editable,
      deletingNode
    } = this.props;
    const { currentCreativeIndex, creatives, displayName } = this.state;
    const currentCreative = creatives[currentCreativeIndex] || {};
    const {
      key_values: keyValues = { keys: [], values: [] }
    } = currentCreative;
    const currentApp = apps[currentCreativeIndex] || {};
    const { iconURL, name: appName } = currentApp;

    if (deletingNode) {
      return (
        <DeleteNodeModal
          cancelModal={this.toggleShowDeleteModal}
          closeModal={this.close}
        />
      );
    }

    return (
      <div>
        <div className="PushNodeModal__Title">
          <svg
            style={{
              height: '60px',
              width: '60px',
              'min-width': '60px'
            }}
          >
            <StandardNode
              height={height}
              rx={rx}
              ry={ry}
              icon={MESSAGE_ICON}
              iconFill={MESSAGE_FILL_COLOR}
              botLeftRounded={false}
            />
          </svg>
          <div className="PushNodeModal__MessageNameContainer">
            <input
              type="text"
              className="PushNodeModal__MessageNameInput"
              value={displayName}
              onChange={this.updateDisplayName}
              onBlur={this.checkForEmptyName}
              disabled={!editable}
              ref={this.focusMessageNameInput}
            />
          </div>
          <button
            className="PushNodeModal__CloseButton"
            onClick={this.close}
            tabIndex="-1"
          >
            &times;
          </button>
        </div>
        <div className="PushNodeModal__Content">
          <div className="PushNodeModal__ContentLeft">
            <div className="PushNodeModal__AppTabContainer">
              {this.props.apps.map((app, index) => {
                const selected = currentCreativeIndex === index;
                return (
                  <div
                    key={app.appId}
                    className={classnames('PushNodeModal__AppTab', {
                      'PushNodeModal__AppTab--selected': selected
                    })}
                    tabIndex="-1"
                    role="button"
                    onClick={() => this.switchCreatives(index)}
                    onKeyPress={() => {}}
                  >
                    <img
                      src={app.iconURL}
                      className="PushNodeModal__AppTabIcon"
                      alt=""
                    />
                    <div className="PushNodeModal__AppTabText">{app.name}</div>
                  </div>
                );
              })}
            </div>
            <Editor
              form={currentCreative}
              keyValues={keyValues}
              submitFormField={this.submitFormField}
              updateKVKeys={this.updateKVKeys}
              updateKVValues={this.updateKVValues}
              addEmptyKeyValue={this.addEmptyKeyValue}
              removeKeyValue={this.removeKeyValue}
              disabled={!editable}
              supportsTemplating={false}
            />
          </div>
          <div className="PushNodeModal__ContentRight">
            <Preview
              form={currentCreative}
              appIconUrl={iconURL}
              appName={appName}
            />
          </div>
          {nodeExistsInFlow && (
            <button
              className={classnames('PushNodeModal__DeleteNodeButton', {
                hidden: !editable
              })}
              tabIndex={-1}
              onClick={this.toggleShowDeleteModal}
            >
              Delete this message
            </button>
          )}
        </div>
        <div
          className={classnames('PushNodeModal__ControlButtons', {
            hidden: !editable
          })}
        >
          <div>
            <Button tabIndex={-1} onClick={this.cancel}>
              Cancel
            </Button>
            <Button tabIndex={-1} type="primary" onClick={this.submitForm}>
              {nodeExistsInFlow ? `Update` : `Add`}
            </Button>
          </div>
        </div>
        <ConfirmationDialog
          showConfirmationDialog={this.state.showConfirmationDialog}
          onCancel={this.hideConfirmationDialog}
          onDontSave={this.closeForReal}
          onSave={this.submitForm}
          nodeTitle="the Push Event"
        />
      </div>
    );
  }
}

const mapStateToProp = state => {
  const { tree: { selectedNode, nodesList, deletingNode } } = state;
  const node = nodesList.find(n => n.data.name === selectedNode);
  const nodeExistsInFlow = node && node.data.node_type !== ADD_NODE_TYPE;
  return {
    ...state,
    nodeExistsInFlow,
    deletingNode
  };
};

export default connect(mapStateToProp)(PushNodeModal);



// WEBPACK FOOTER //
// ./src/components/PushNodeModal.jsx